import LkNotificationsApi from '@/services/api/lk-notifications';
import {
  normalizeLkNotificationAdminItem,
  resolveLkNotificationId,
} from '@/constants/lk-notifications';

export default {
  namespaced: true,
  state: {
    items: [],
    unreadCount: 0,
    loading: false,
    broadcasts: [],
    broadcastsLoading: false,
  },
  getters: {
    items: state => state.items,
    unreadCount: state => state.unreadCount,
    loading: state => state.loading,
    unreadItems: state => state.items.filter(item => !item.is_read),
    readItems: state => state.items.filter(item => item.is_read),
    broadcasts: state => state.broadcasts,
    broadcastsLoading: state => state.broadcastsLoading,
  },
  mutations: {
    setItems (state, items) {
      state.items = items;
    },
    setUnreadCount (state, count) {
      state.unreadCount = count;
    },
    setLoading (state, loading) {
      state.loading = loading;
    },
    setBroadcasts (state, broadcasts) {
      state.broadcasts = broadcasts;
    },
    setBroadcastsLoading (state, loading) {
      state.broadcastsLoading = loading;
    },
    removeNotification (state, id) {
      state.broadcasts = state.broadcasts.filter(
        item => resolveLkNotificationId(item) !== id,
      );
    },
    markItemRead (state, { id, readAt }) {
      const item = state.items.find(n => n.id === id);
      if (item) {
        item.is_read = true;
        item.read_at = readAt;
      }
      if (state.unreadCount > 0) {
        state.unreadCount -= 1;
      }
    },
    reset (state) {
      state.items = [];
      state.unreadCount = 0;
      state.loading = false;
    },
  },
  actions: {
    fetchUnreadCount ({ commit }) {
      return LkNotificationsApi.list({ limit: 1, read: false })
        .then(({ data }) => {
          const count = data.meta?.unread_count ?? 0;
          commit('setUnreadCount', count);
          return count;
        })
        .catch(() => {
          commit('setUnreadCount', 0);
        });
    },

    fetchList ({ commit }, { limit = 50 } = {}) {
      commit('setLoading', true);
      return LkNotificationsApi.list({ limit })
        .then(({ data }) => {
          commit('setItems', data.data || []);
          commit('setUnreadCount', data.meta?.unread_count ?? 0);
          return data;
        })
        .catch((e) => {
          commit('setItems', []);
          throw e;
        })
        .finally(() => {
          commit('setLoading', false);
        });
    },

    markRead ({ commit, dispatch }, id) {
      return LkNotificationsApi.markRead(id)
        .then(({ data }) => {
          commit('markItemRead', {
            id,
            readAt: data.data?.read_at,
          });
          return dispatch('fetchUnreadCount');
        });
    },

    markAllRead ({ commit, dispatch }) {
      return LkNotificationsApi.markAllRead()
        .then(() => {
          commit('setUnreadCount', 0);
          return dispatch('fetchList');
        });
    },

    create ({ dispatch }, payload) {
      return LkNotificationsApi.create(payload)
        .then((response) => {
          dispatch('fetchUnreadCount');
          return response;
        });
    },

    fetchBroadcasts ({ commit }, options = {}) {
      commit('setBroadcastsLoading', true);
      return LkNotificationsApi.listAll(options)
        .then(({ data }) => {
          const raw = data.data ?? data.notifications ?? data;
          const list = Array.isArray(raw) ? raw : [];
          commit('setBroadcasts', list.map(normalizeLkNotificationAdminItem));
          return data;
        })
        .catch((e) => {
          commit('setBroadcasts', []);
          throw e;
        })
        .finally(() => {
          commit('setBroadcastsLoading', false);
        });
    },

    destroyNotification ({ commit, dispatch }, itemOrId) {
      const id = typeof itemOrId === 'object'
        ? resolveLkNotificationId(itemOrId)
        : itemOrId;

      if (id == null) {
        return Promise.reject(new Error('Не указан id уведомления'));
      }

      return LkNotificationsApi.destroy(id)
        .then((response) => {
          commit('removeNotification', id);
          dispatch('fetchUnreadCount');
          return response;
        });
    },

    destroyReadNotifications ({ dispatch }) {
      return LkNotificationsApi.destroyRead()
        .then((response) => {
          dispatch('fetchUnreadCount');
          return dispatch('fetchBroadcasts').then(() => response);
        });
    },
  },
};
